import FormHeader from "@/components/FormHeader";
import SkeletonRow from "@/components/SkeletonRow";
import { Button } from "@/components/ui/button";
import { Checkbox } from "@/components/ui/checkbox";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { Textarea } from "@/components/ui/textarea";
import api from "@/lib/axios";
import {
  getListJenjangPendidikan,
  getListProgramStudentExchange,
} from "@/services/getData";
import {
  JalurJenis,
  JenjangPendidikan,
  Periode,
  RefDaftarProgram,
} from "@/types";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { AxiosError } from "axios";
import { Loader2 } from "lucide-react";
import { useEffect, useState } from "react";
import toast from "react-hot-toast";
import { FaPlus } from "react-icons/fa6";
import { MdModeEdit } from "react-icons/md";

const postData = async (newData: Periode) => {
  const response = await api.post("/setting/periode/store", newData);
  return response.data;
};

const patchData = async (newData: Periode) => {
  const response = await api.patch("/setting/periode/update", newData);
  return response.data;
};

const deleteData = async (deletedData: { id_sys_periode: string }) => {
  const response = await api({
    url: "/setting/periode/delete",
    method: "DELETE",
    data: deletedData,
  });
  return response.data;
};

const getListJalurJenis = async () => {
  const response = await api.get("/master/jalur-jenis/index", {
    withCredentials: true,
  });
  return response.data?.data;
};

export default function Form({
  method,
  selectedRow,
  closeModal,
}: {
  method: "POST" | "PATCH" | "DELETE";
  selectedRow?: Periode | null;
  closeModal: () => void;
}) {
  const { data: dataJalurJenis } = useQuery({
    queryKey: ["listJalurJenis"],
    queryFn: getListJalurJenis,
  });
  const filteredJalurJenis = dataJalurJenis
    ? dataJalurJenis.filter(
        (jalur: JalurJenis) =>
          jalur.nama_jalur_jenis_slug === "studentexchange",
      )
    : [];

  const jalurStudentExchange =
    dataJalurJenis &&
    dataJalurJenis.find((jalur: JalurJenis) => {
      return jalur.nama_jalur_jenis_slug === "studentexchange";
    });

  const {
    data: dataJenjangPendidikan,
    // isPending: isPendingDataJenjangPendidikan,
  } = useQuery({
    queryKey: ["listJenjangPendidikan"],
    queryFn: getListJenjangPendidikan,
  });
  const jenjangLainnya =
    dataJenjangPendidikan &&
    dataJenjangPendidikan.find((jenjang: JenjangPendidikan) => {
      return jenjang.id_feeder == 99;
    });

  const {
    data: dataProgramStudentExchange,
    isPending: isPendingDataProgramStudentExchange,
  } = useQuery({
    queryKey: ["listProgramStudentExchange"],
    queryFn: getListProgramStudentExchange,
  });

  const [form, setForm] = useState<
    Periode & { daftar_program: { id_ref_daftar_program: string }[] }
  >({
    id_sys_periode: selectedRow?.id_sys_periode || "",
    id_ref_jalur_jenis: selectedRow?.id_ref_jalur_jenis || "",
    tahun: selectedRow?.tahun || "",
    gelombang: selectedRow?.gelombang || "",
    tahun_ajaran_masuk: selectedRow?.tahun_ajaran_masuk || "",
    semester_masuk: selectedRow?.semester_masuk || "",
    waktu_mulai_daftar: selectedRow?.waktu_mulai_daftar || "",
    waktu_selesai_daftar: selectedRow?.waktu_selesai_daftar || "",
    waktu_selesai_daftar_valid: selectedRow?.waktu_selesai_daftar_valid || "",
    waktu_pengumuman_kelulusan: selectedRow?.waktu_pengumuman_kelulusan || "",
    waktu_mulai_isi_ukt: selectedRow?.waktu_mulai_isi_ukt || "",
    waktu_selesai_isi_ukt: selectedRow?.waktu_selesai_isi_ukt || "",
    waktu_pengumuman: selectedRow?.waktu_pengumuman || "",
    waktu_mulai_daftarkembali: selectedRow?.waktu_mulai_daftarkembali || "",
    waktu_selesai_daftarkembali: selectedRow?.waktu_selesai_daftarkembali || "",
    waktu_mulai_sanggah_berkas_daftarkembali:
      selectedRow?.waktu_mulai_sanggah_berkas_daftarkembali || "",
    waktu_selesai_sanggah_berkas_daftarkembali:
      selectedRow?.waktu_selesai_sanggah_berkas_daftarkembali || "",
    waktu_mulai_sanggah_kelulusan:
      selectedRow?.waktu_mulai_sanggah_kelulusan || "",
    waktu_selesai_sanggah_kelulusan:
      selectedRow?.waktu_selesai_sanggah_kelulusan || "",
    waktu_pengumuman_sanggah_kelulusan:
      selectedRow?.waktu_pengumuman_sanggah_kelulusan || "",
    waktu_pengumuman_kelulusan_cadangan:
      selectedRow?.waktu_pengumuman_kelulusan_cadangan || "",
    waktu_mulai_daftarkembali_cadangan:
      selectedRow?.waktu_mulai_daftarkembali_cadangan || "",
    waktu_selesai_daftarkembali_cadangan:
      selectedRow?.waktu_selesai_daftarkembali_cadangan || "",
    tanggal_mulai_kuliah: selectedRow?.tanggal_mulai_kuliah || "",
    link_telegram: selectedRow?.link_telegram || "",
    link_telegram_pkkmb: selectedRow?.link_telegram_pkkmb || "",
    jenjang_buka: selectedRow?.jenjang_buka || [],
    biaya_daftar: selectedRow?.biaya_daftar || "",
    biaya_portofolio: selectedRow?.biaya_portofolio || "",
    waktu_mulai_bayar_ukt: selectedRow?.waktu_mulai_bayar_ukt || "",
    waktu_selesai_bayar_ukt: selectedRow?.waktu_selesai_bayar_ukt || "",
    tanggal_cbt: selectedRow?.tanggal_cbt || [],
    is_cek_jurusan_sekolah: selectedRow?.is_cek_jurusan_sekolah ?? false,
    waktu_tkb_pasca: selectedRow?.waktu_tkb_pasca || "",
    waktu_tpa_pasca: selectedRow?.waktu_tpa_pasca || "",
    daftar_program:
      selectedRow?.sys_daftar_program?.map((sys) => {
        return { id_ref_daftar_program: sys.id_ref_daftar_program };
      }) || [],
    keterangan: selectedRow?.keterangan || "",
  });

  useEffect(() => {
    if (!jalurStudentExchange) return;

    setForm((prev) => ({
      ...prev,
      id_ref_jalur_jenis: String(jalurStudentExchange.id_ref_jalur_jenis),
    }));
  }, [jalurStudentExchange]);

  const isPost = method === "POST";
  const isPatch = method === "PATCH";

  const queryClient = useQueryClient();
  const { mutate, isPending } = useMutation({
    mutationFn: isPost ? postData : isPatch ? patchData : deleteData,
    onSuccess: (data) => {
      queryClient.invalidateQueries({
        queryKey: ["listPeriodeStudentExchange"],
      });
      closeModal();
      toast.success(data?.message || "Success.");
    },
    onError: (error: AxiosError) => {
      const err = error as AxiosError<{ message?: string }>;
      toast.error(
        err?.response?.data?.message || "An unexpected error occurred.",
      );
    },
  });

  const handleSubmit = async (event: React.FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    mutate({ ...form, jenjang_buka: [jenjangLainnya] });
  };

  return (
    <form onSubmit={handleSubmit} className="flex flex-col gap-y-3">
      <FormHeader
        icon={method === "POST" ? <FaPlus /> : <MdModeEdit />}
        title={isPost ? "Insert Data Periode" : "Update Data Periode"}
      />
      <section className="grid grid-cols-3 gap-5">
        <div>
          <Label>Ref Jalur Jenis</Label>
          <Select
            required
            value={form.id_ref_jalur_jenis}
            onValueChange={(value) => {
              if (!value) return;
              setForm((prev) => ({ ...prev, id_ref_jalur_jenis: value }));
            }}
          >
            <SelectTrigger>
              <SelectValue placeholder={"Pilih Ref Jalur"} />
            </SelectTrigger>
            <SelectContent>
              {filteredJalurJenis?.map((jalurJenis: JalurJenis) => (
                <SelectItem
                  key={jalurJenis.id_ref_jalur_jenis}
                  value={`${jalurJenis.id_ref_jalur_jenis}`}
                >
                  {jalurJenis.nama_jalur_jenis}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>

        <div>
          <Label>Tahun</Label>
          <Input
            maxLength={4}
            required
            placeholder="Ketik tahun..."
            value={form.tahun}
            onChange={(event) =>
              setForm((prev) => ({ ...prev, tahun: event.target.value }))
            }
          />
        </div>

        <div>
          <Label>Gelombang</Label>
          <Input
            required
            placeholder="Ketik gelombang..."
            value={form.gelombang}
            onChange={(event) =>
              setForm((prev) => ({
                ...prev,
                gelombang: event.target.value,
              }))
            }
          />
        </div>

        <div>
          <Label>Tahun Ajaran Masuk</Label>
          <Input
            maxLength={4}
            required
            placeholder="Ketik tahun ajaran masuk..."
            value={form.tahun_ajaran_masuk}
            onChange={(event) =>
              setForm((prev) => ({
                ...prev,
                tahun_ajaran_masuk: event.target.value,
              }))
            }
          />
        </div>

        <div>
          <Label>Semester Masuk</Label>
          <Input
            required
            maxLength={1}
            placeholder="Ketik semester masuk..."
            value={form.semester_masuk}
            onChange={(event) =>
              setForm((prev) => ({
                ...prev,
                semester_masuk: event.target.value,
              }))
            }
          />
        </div>

        <div>
          <Label>Waktu Mulai Daftar</Label>
          <Input
            type="datetime-local"
            value={form.waktu_mulai_daftar}
            onChange={(event) =>
              setForm((prev) => ({
                ...prev,
                waktu_mulai_daftar: event.target.value,
              }))
            }
          />
        </div>

        <div>
          <Label>Waktu Selesai Daftar</Label>
          <Input
            type="datetime-local"
            value={form.waktu_selesai_daftar}
            onChange={(event) =>
              setForm((prev) => ({
                ...prev,
                waktu_selesai_daftar: event.target.value,
              }))
            }
          />
        </div>
        <div>
          <Label>Waktu Selesai Validasi Pendaftaran</Label>
          <Input
            type="datetime-local"
            value={form.waktu_selesai_daftar_valid}
            onChange={(event) =>
              setForm((prev) => ({
                ...prev,
                waktu_selesai_daftar_valid: event.target.value,
              }))
            }
          />
        </div>

        <div>
          <Label>Keterangan</Label>
          <Textarea
            value={form.keterangan}
            onChange={(event) =>
              setForm((prev) => ({
                ...prev,
                keterangan: event.target.value,
              }))
            }
          />
        </div>

        {/* <div className="col-span-3">
          <Label>Checklist Jenjang Pendidikan</Label>
          <div className="mt-3 grid grid-cols-4 gap-3">
            {isPendingDataJenjangPendidikan ? (
              Array(20)
                .fill(null)
                .map((_, index) => {
                  return <SkeletonRow key={index} />;
                })
            ) : (
              <>
                {dataJenjangPendidikan?.map(
                  (jenjangPendidikan: JenjangPendidikan) => {
                    return (
                      <div
                        key={jenjangPendidikan.id_ref_jenjang_pendidikan}
                        className="flex items-center gap-3"
                      >
                        <Checkbox
                          checked={form.jenjang_buka?.some(
                            (jenjang) =>
                              jenjang.id_ref_jenjang_pendidikan ===
                              jenjangPendidikan.id_ref_jenjang_pendidikan,
                          )}
                          onCheckedChange={() => {
                            setForm((prevForm) => {
                              const jenjang_buka = prevForm.jenjang_buka || [];

                              const isChecked = jenjang_buka?.some(
                                (jenjang) =>
                                  jenjang.id_ref_jenjang_pendidikan ==
                                  jenjangPendidikan.id_ref_jenjang_pendidikan,
                              );

                              const updatedJenjangBuka = isChecked
                                ? jenjang_buka.filter(
                                    (jenjang) =>
                                      jenjang.id_ref_jenjang_pendidikan !==
                                      jenjangPendidikan.id_ref_jenjang_pendidikan,
                                  )
                                : [...jenjang_buka, jenjangPendidikan];

                              return {
                                ...prevForm,
                                jenjang_buka: updatedJenjangBuka,
                              };
                            });
                          }}
                        />
                        <Label>
                          {jenjangPendidikan.nama_jenjang_pendidikan}
                        </Label>
                      </div>
                    );
                  },
                )}
              </>
            )}
          </div>
        </div> */}

        <div className="col-span-3">
          <Label>Checklist Daftar Program</Label>
          <div className="mt-3 grid grid-cols-4 gap-3">
            {isPendingDataProgramStudentExchange ? (
              Array(20)
                .fill(null)
                .map((_, index) => {
                  return <SkeletonRow key={index} />;
                })
            ) : (
              <>
                {dataProgramStudentExchange?.map(
                  (program: RefDaftarProgram) => {
                    return (
                      <div
                        key={program.id_ref_daftar_program}
                        className="flex items-center gap-3"
                      >
                        <Checkbox
                          checked={form.daftar_program.some(
                            (item) =>
                              item.id_ref_daftar_program ===
                              program.id_ref_daftar_program,
                          )}
                          onCheckedChange={() => {
                            setForm((prevForm) => {
                              const selectedDaftarProgram =
                                prevForm.daftar_program;

                              const isChecked = selectedDaftarProgram.some(
                                (item) =>
                                  item.id_ref_daftar_program ===
                                  program.id_ref_daftar_program,
                              );

                              const updatedDaftarProgram = isChecked
                                ? selectedDaftarProgram.filter(
                                    (item) =>
                                      item.id_ref_daftar_program !==
                                      program.id_ref_daftar_program,
                                  )
                                : [
                                    ...selectedDaftarProgram,
                                    {
                                      id_ref_daftar_program:
                                        program.id_ref_daftar_program,
                                    },
                                  ];

                              return {
                                ...prevForm,
                                daftar_program: updatedDaftarProgram,
                              };
                            });
                          }}
                        />
                        <Label>{program.nama_program}</Label>
                      </div>
                    );
                  },
                )}
              </>
            )}
          </div>
        </div>
      </section>

      <div className="mt-3 flex gap-3">
        <Button type="button" variant={"outline"} onClick={() => closeModal()}>
          Batal
        </Button>
        <Button className="flex items-center gap-3" disabled={isPending}>
          {isPending ? (
            <>
              <Loader2 className="w-4 h-4 mr-1 animate-spin" />
              Processing...
            </>
          ) : (
            "Simpan"
          )}
        </Button>
      </div>
    </form>
  );
}
